import { create } from 'zustand'
import type {
  Ship,
  ShipType,
  CellResult,
  GamePhase,
  CellState,
} from '@fleetduel/shared'
import { coordKey } from '@fleetduel/shared'

export interface GameStore {
  // Connection state
  roomId: string | null
  playerIndex: 0 | 1 | null
  phase: GamePhase
  currentTurn: 0 | 1
  winner: 0 | 1 | null

  // Player's own ships
  myShips: Ship[]

  // What we know about opponent's board (our attacks)
  opponentBoard: Map<string, CellState>

  // What opponent has done to our board
  myBoardHits: Map<string, 'hit' | 'miss'>

  // Charges
  torpedoCharges: number
  sonarCharges: number

  // Sunk opponent ships
  sunkenShips: ShipType[]

  // Game over boards
  revealedBoards: [Ship[], Ship[]] | null
  revealedHits: [CellResult[], CellResult[]] | null

  // Actions
  setRoomId: (id: string) => void
  setPlayerIndex: (idx: 0 | 1) => void
  setPhase: (phase: GamePhase) => void
  setCurrentTurn: (turn: 0 | 1) => void
  setMyShips: (ships: Ship[]) => void
  applyActorResult: (results: CellResult[], sunkShip: ShipType | null, torpedoCharges: number, sonarCharges: number, sonarCount: number | undefined) => void
  applyObserverResult: (actionType: string, cells: CellResult[], sunkShip: ShipType | null) => void
  setGameOver: (winner: 0 | 1, boards: [Ship[], Ship[]], boardHits: [CellResult[], CellResult[]]) => void
  reset: () => void
}

const initialState = {
  roomId: null as string | null,
  playerIndex: null as 0 | 1 | null,
  phase: 'SETUP' as GamePhase,
  currentTurn: 0 as 0 | 1,
  winner: null as 0 | 1 | null,
  myShips: [] as Ship[],
  opponentBoard: new Map<string, CellState>(),
  myBoardHits: new Map<string, 'hit' | 'miss'>(),
  torpedoCharges: 2,
  sonarCharges: 2,
  sunkenShips: [] as ShipType[],
  revealedBoards: null as [Ship[], Ship[]] | null,
  revealedHits: null as [CellResult[], CellResult[]] | null,
}

export const useGameStore = create<GameStore>((set) => ({
  ...initialState,

  setRoomId: (id) => set({ roomId: id }),
  setPlayerIndex: (idx) => set({ playerIndex: idx }),
  setPhase: (phase) => set({ phase }),
  setCurrentTurn: (turn) => set({ currentTurn: turn }),
  setMyShips: (ships) => set({ myShips: ships }),

  applyActorResult: (results, sunkShip, torpedoCharges, sonarCharges, _sonarCount) =>
    set((state) => {
      const newBoard = new Map(state.opponentBoard)
      for (const r of results) {
        newBoard.set(coordKey(r.coord), r.hit ? 'hit' : 'miss')
      }
      if (sunkShip) {
        // Mark sunk cells
        for (const r of results) {
          if (r.hit) newBoard.set(coordKey(r.coord), 'sunk')
        }
      }
      return {
        opponentBoard: newBoard,
        torpedoCharges,
        sonarCharges,
        sunkenShips: sunkShip ? [...state.sunkenShips, sunkShip] : state.sunkenShips,
      }
    }),

  applyObserverResult: (_actionType, cells, _sunkShip) =>
    set((state) => {
      const newHits = new Map(state.myBoardHits)
      for (const c of cells) {
        newHits.set(coordKey(c.coord), c.hit ? 'hit' : 'miss')
      }
      return { myBoardHits: newHits }
    }),

  setGameOver: (winner, boards, boardHits) => set({ phase: 'GAME_OVER', winner, revealedBoards: boards, revealedHits: boardHits }),

  reset: () => set({ ...initialState, opponentBoard: new Map(), myBoardHits: new Map() }),
}))
